import { Injectable } from '@angular/core';
import { IntervalBase, PaymentInterval } from '@fmnts/api/next';
import { isNil, Nullish } from '@fmnts/core';
import * as Decimal from '@fmnts/core/decimal';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFieldComponent,
  FieldNodeInputDefinitionList,
  FormDeclaration,
  FormSelectField,
  GraphBuilderContext,
  GraphInputDefinition,
  OperatorNodeDefinition,
} from '@fmnts/declarative-forms';
import {
  FormGraphNodeBuilder,
  FormOperatorName,
  RxJsNodeName,
  RxJsOperatorNodeName,
} from '@fmnts/declarative-forms/common';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import { combineLatest, map, Observable, OperatorFunction } from 'rxjs';
import {
  NextFieldKey,
  nextFieldPath,
  NextFieldsetKey,
} from '../next-form-known-fields';
import {
  SponsorshipNodeName,
  SponsorshipOperatorName,
} from '../sponsorship/sponsorship-node-builder.service';
import {
  DonationAmountService,
  IntervalAmount,
} from './donation-amount.service';

export enum DonationAmountNodeName {
  PaymentAmount = 'amount.payment_amount$',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  PaymentInterval = 'amount.payment_interval$',
  AmountPerInterval = 'amount.amount_per_interval$',
}

export enum DonationAmountOperatorNodeName {
  MapIntervalAmountToPaymentInterval = 'amount.map.interval_amount.payment_interval',
  RoundToDecimalPlaces = 'amount.round.to_decimal_places',
}

const fakeDonationAmountPerIntervalDefinition: FieldNodeInputDefinitionList = {
  value: {
    name: DonationAmountNodeName.AmountPerInterval,
    pipe: [
      // TODO: Maybe move rounding to the UI?
      {
        operator: DonationAmountOperatorNodeName.RoundToDecimalPlaces,
        args: [2],
      },
    ],
  },
};

/*
 **
 * Graph, that emits the min amount whenever the
 * selected sponsorship item changes and the 'set_min_amount'
 * property is `true`
 */
const setMinAmountFromSponsorship: GraphInputDefinition = {
  name: SponsorshipNodeName.ItemChanges,
  pipe: [
    { operator: SponsorshipOperatorName.FilterSetMinAmount, args: [true] },
    { operator: RxJsOperatorNodeName.Pluck, args: ['min_amount'] },
    {
      operator: FormOperatorName.SetUiState,
      args: [{ dirty: true }],
    },
  ],
};

/**
 * Graph, that emits the min amount (transformed to last chosen interval)
 * whenever the selected sponsorship item changes and the
 * 'set_min_amount' property is `true`.
 */
const setMinAmountFromSponsorshipPerInterval: GraphInputDefinition = {
  name: SponsorshipNodeName.ItemChanges,
  pipe: [
    { operator: SponsorshipOperatorName.FilterSetMinAmount, args: [true] },
    {
      operator: SponsorshipOperatorName.MapItemToIntervalAmount,
      args: ['min_amount'],
    },
    {
      operator: RxJsOperatorNodeName.WithLatestFrom,
      args: [
        {
          name: DonationAmountNodeName.PaymentInterval,
        },
      ],
    },
    {
      operator:
        DonationAmountOperatorNodeName.MapIntervalAmountToPaymentInterval,
    },
    {
      operator: FormOperatorName.SetUiState,
      args: [{ dirty: true }],
    },
  ],
};

/**
 * Builder for donation amount related graph nodes.
 */
@Injectable({
  providedIn: 'root',
})
export class DonationAmountGraphNodeBuilder extends AbstractGraphNodeBuilder<
  DonationAmountNodeName,
  DonationAmountOperatorNodeName
> {
  constructor(
    private formSource: FormGraphNodeBuilder,
    private donationAmountService: DonationAmountService,
  ) {
    super();

    // Add nodes
    this.addNode(DonationAmountNodeName.PaymentAmount, (_, ctx) =>
      this.paymentAmount$(ctx),
    )
      .addNode(DonationAmountNodeName.PaymentInterval, (_, ctx) =>
        this.paymentInterval$(ctx),
      )
      .addNode(DonationAmountNodeName.AmountPerInterval, (_, ctx) =>
        this.amountPerInterval$(ctx),
      );

    // Add operators
    this.addOperator(
      DonationAmountOperatorNodeName.MapIntervalAmountToPaymentInterval,
      () => this.mapIntervalAmountToPaymentInterval(),
    ).addOperator(
      DonationAmountOperatorNodeName.RoundToDecimalPlaces,
      (def: OperatorNodeDefinition<[number]>) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.roundToDecimalPlaces(def.args?.[0]),
    );
  }

  /**
   * @param ctx
   *
   * @returns
   * `Observable` that emits the payment amount.
   * May emit `null`, if the input has no value (e.g. when resetted).
   */
  private paymentAmount$(
    ctx: GraphBuilderContext,
  ): Observable<Decimal.Decimal | null> {
    return this.formSource
      .fieldValue$({ path: nextFieldPath.donationAmount }, ctx)
      .pipe(map(Fn.flow(Decimal.fromUnknown, O.getOrNull)));
  }

  /**
   * @param ctx
   *
   * @returns
   * `Observable` that emits the payment interval.
   * May emit `null`, if the input has no value (e.g. when resetted).
   */
  private paymentInterval$(
    ctx: GraphBuilderContext,
  ): Observable<PaymentInterval | Nullish> {
    return this.formSource.fieldValue$(
      { path: nextFieldPath.directDebitInterval },
      ctx,
    );
  }

  /**
   * @param ctx
   *
   * @returns
   * `Observable` that emits the payment amount per interval.
   * May emit `null`, if the input has no value (e.g. when resetted).
   */
  private amountPerInterval$(ctx: GraphBuilderContext) {
    const genericField = ctx.injector.get(DeclarativeFieldComponent, null);
    const calculationBase = genericField?.field?.attrs?.['calculation_base'];
    const declaration = genericField?.declaration;

    if (isNil(calculationBase) || !declaration) {
      return;
    }

    // In case that the campaign uses the "display amounts per interval" option, the values that are emitted
    // are already per interval
    const amountFieldUsesPerInterval =
      declaration.get(nextFieldPath.donationAmount)?.attrs?.[
        'show_per_interval'
      ] ?? false;
    if (amountFieldUsesPerInterval) {
      return this.paymentAmount$(ctx);
    }

    return combineLatest([
      this.paymentAmount$(ctx),
      this.paymentInterval$(ctx),
    ]).pipe(this.toInterval(calculationBase));
  }

  /**
   * @returns
   * `Observable` that takes in the emission of an
   * `IntervalAmount` and `PaymentInterval` and that emits
   * that amount transformed to the given interval.
   */
  private mapIntervalAmountToPaymentInterval(): OperatorFunction<
    [IntervalAmount, PaymentInterval],
    Decimal.Decimal
  > {
    return map(([intervalAmount, targetInterval]) =>
      this.donationAmountService.toInterval(
        intervalAmount.amount,
        intervalAmount.base,
        targetInterval,
      ),
    );
  }

  /**
   * @param decimalPlaces Number of decimal places rounded to
   *
   * @returns
   * Operator that takes in an amount and emits the rounded
   * value whereas the rounding is done according to the given
   * rounding options.
   */
  private roundToDecimalPlaces(
    decimalPlaces: number | undefined,
  ): OperatorFunction<unknown, string | null> {
    const round = (value: Decimal.Decimal) =>
      value.toDecimalPlaces(decimalPlaces).toString();

    return map((amount) => (Decimal.isDecimal(amount) ? round(amount) : null));
  }

  /**
   * @param sourceBase The base used to interpret the amount
   *
   * @returns
   * Operator that takes in an `amount` (interpreted based on `sourceBase`)
   * and a `targetInterval` and that emits the new amount by converting
   * it from `sourceBase` to `targetInterval`.
   * If one of the sources emit a non convertable value, `null` will be
   * emitted.
   *
   * @example
   * // Will emit `new Decimal(2)`
   * of([new Decimal(1), PaymentInterval.Yearly]).pipe(
   *    this.toInterval(IntervalBase.HalfYearly)
   * );
   */
  private toInterval(
    sourceBase: IntervalBase,
  ): OperatorFunction<
    [Decimal.Decimal | unknown, PaymentInterval | Nullish],
    Decimal.Decimal | null
  > {
    return map(([amount, targetInterval]) =>
      // There may be null-like values when the form is resetted
      !Decimal.isDecimal(amount) || isNil(targetInterval)
        ? null
        : this.donationAmountService.toInterval(
            amount,
            sourceBase,
            targetInterval,
          ),
    );
  }

  createFakeDefinitions(ctx: GraphBuilderContext) {
    const genericComponent = ctx.injector.get(DeclarativeFieldComponent, null);
    const field = genericComponent?.field;
    const fieldset = genericComponent?.fieldset;

    // Check for valid places to add fake definitions
    if (!field || fieldset?.key !== NextFieldsetKey.Contribution) {
      return;
    }

    switch (field.key) {
      case NextFieldKey.DonationAmountPerInterval:
        return fakeDonationAmountPerIntervalDefinition;
      case NextFieldKey.DonationAmount:
        const donationAmountValueGraphs = createFakeDonationAmountValue(
          field,
          genericComponent.declaration,
        );
        return donationAmountValueGraphs.length === 0
          ? undefined
          : {
              value: {
                name: RxJsNodeName.Merge,
                options: donationAmountValueGraphs,
              },
            };
    }
  }
}

function createFakeDonationAmountValue(
  field: FormSelectField,
  declaration: FormDeclaration,
): GraphInputDefinition[] {
  // All available graphs are currently related to the sponsorship field
  // so only build them if the sponsorship field is available
  const sponsorshipField = declaration.get(nextFieldPath.sponsorship);
  if (!sponsorshipField) {
    return [];
  }

  // This flag determines if the donation amount handles it's amount to be
  // per interval
  const showPerInterval = field?.attrs?.['show_per_interval'] ?? false;
  if (showPerInterval) {
    return [setMinAmountFromSponsorshipPerInterval];
  } else {
    return [setMinAmountFromSponsorship];
  }
}
